import { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Repeat } from 'lucide-react';
import { recurringPaymentsCoverageAPI } from '../../lib/api/recurringPayments';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobProgressLine, JobErrorBlock } from './JobPollingPanel';

// Keep in sync with backend/services/recurring_payments_coverage.py's
// ALL_CLASSES -- labels/colors only, classification values themselves are
// never recomputed here.
const CLASS_META = {
  matched: { label: 'Matched', short: 'OK', className: 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300' },
  suggested: { label: 'Suggested (unconfirmed match)', short: '?OK', className: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300' },
  tx_present_no_doc: { label: 'No doc (mailbox/parse gap)', short: 'TX', className: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' },
  doc_unparseable: { label: 'Doc amount-quarantined', short: 'QT', className: 'bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300' },
  both_present_unmatched: { label: 'Unmatched (matcher gap)', short: '!!', className: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300' },
  doc_present_no_tx: { label: 'No tx (feed gap / unpaid)', short: 'DOC', className: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-300' },
  refund_anomaly: { label: 'Refund anomaly', short: '⚠', className: 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300' },
};

const HEADLINE_CLASS = 'both_present_unmatched';

// A matched cell settled by a non-invoice matcher (recurring_cost /
// management_statement -- see backend/services/recurring_payments_coverage.py's
// "Cross-matcher settlement" docstring section) carries settled_via. It is
// still classification "matched" (counts in the Matched chip, never a
// gap) -- this only adjusts the label/short text so the grid stays honest
// about HOW it was settled, never a new top-level cell state.
function _cellMeta(cell) {
  const base = CLASS_META[cell.classification] || { short: '?', className: '' };
  if (cell.classification === 'matched' && cell.settled_via) {
    return {
      ...base,
      label: `${base.label} (settled via ${cell.settled_via.replace(/_/g, ' ')}, no doc link)`,
      short: 'OK·',
    };
  }
  return base;
}

function Cell({ vendorKey, month, cell, selected, onSelect }) {
  if (!cell) return <td className="p-1 text-center text-muted-foreground">&middot;</td>;
  const meta = _cellMeta(cell);
  return (
    <td className="p-1 text-center">
      <button
        type="button"
        onClick={() => onSelect(vendorKey, month, cell)}
        title={meta.label}
        data-testid={`rpc-cell-${vendorKey}-${month}`}
        className={
          'inline-flex h-6 min-w-[2.25rem] items-center justify-center rounded px-1 text-[10px] font-medium ' +
          meta.className + (selected ? ' ring-2 ring-offset-1 ring-primary' : '')
        }
      >
        {meta.short}
      </button>
    </td>
  );
}

export function RecurringPaymentsCoveragePanel() {
  const [result, setResult] = useState(null);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(null);
  const [progress, setProgress] = useState(null);
  const [selected, setSelected] = useState(null);

  const poll = useJobPolling({
    fetchStatus: recurringPaymentsCoverageAPI.jobStatus,
    onCompleted: useCallback((data) => {
      setResult(data);
      setFailed(null);
      setProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setFailed(error);
      setProgress(null);
    }, []),
    onProgress: useCallback((p) => setProgress(p), []),
  });

  useJobResume({ fetchLatest: useCallback(() => recurringPaymentsCoverageAPI.jobLatest(), []), poll });

  const runNow = async () => {
    setStarting(true);
    setFailed(null);
    setProgress(null);
    try {
      const { data } = await recurringPaymentsCoverageAPI.start();
      if (data.job_id) poll.start(data.job_id);
    } catch (err) {
      setFailed(getErrorMessage(err) || 'Failed to start the census run.');
    } finally {
      setStarting(false);
    }
  };

  const busy = starting || poll.polling;

  const onSelectCell = (vendorKey, month, cell) => {
    setSelected({ vendorKey, month, cell });
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Repeat className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Recurring Payments Coverage</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Every recurring vendor (subscription-tagged transactions, plus any vendor with 3+
          invoices/receipts in the last 12 months) gridded against the last 12 months, classified
          from existing data only -- never recomputes matching. "Unmatched" is the real matcher gap
          to drive to zero; "doc unparseable" means the doc is amount-quarantined, not genuinely
          missing; "refund anomaly" (e.g. an inbound payment in an outflow series) always surfaces,
          even on an otherwise-matched cell. Click a cell for its transaction/doc ids.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          variant="outline" size="sm" onClick={runNow} disabled={busy}
          data-testid="rpc-run-now"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Run Now
        </Button>

        {poll.polling && (
          <JobProgressLine progress={progress} testId="rpc-progress">
            {(p) => <>Scanned {p.tx_count ?? 0} transactions, {p.doc_count ?? 0} docs</>}
          </JobProgressLine>
        )}

        <JobErrorBlock error={failed} testId="rpc-error" />

        {result && (
          <div className="space-y-3" data-testid="rpc-result">
            <p className="text-xs text-muted-foreground" data-testid="rpc-summary">
              {result.vendor_count} recurring vendor(s) &middot; generated {result.generated_at}
              {(result.tx_scan_truncated || result.doc_scan_truncated || result.match_scan_truncated) && (
                <span className="text-amber-700 dark:text-amber-400">
                  {' '}&middot; scan cap hit ({[
                    result.tx_scan_truncated && 'transactions',
                    result.doc_scan_truncated && 'docs',
                    result.match_scan_truncated && 'matches',
                  ].filter(Boolean).join(' & ')}) -- result may be incomplete
                </span>
              )}
            </p>

            <div className="flex flex-wrap gap-2">
              {Object.entries(result.totals || {}).map(([cls, count]) => (
                <Badge
                  key={cls}
                  variant="outline"
                  className={'text-[10px] ' + (cls === HEADLINE_CLASS ? 'font-semibold' : '')}
                  data-testid={`rpc-total-${cls}`}
                >
                  {(CLASS_META[cls] || {}).label || cls}: {count}
                </Badge>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="text-xs border-collapse" data-testid="rpc-grid">
                <thead>
                  <tr>
                    <th className="text-left pr-3 pb-1 sticky left-0 bg-background">Vendor</th>
                    <th className="text-right pr-3 pb-1">Gaps</th>
                    {(result.months || []).map((m) => (
                      <th key={m} className="px-1 pb-1 font-normal text-muted-foreground">{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(result.vendors || []).map((row) => (
                    <tr key={row.vendor_key} className="border-t" data-testid={`rpc-row-${row.vendor_key}`}>
                      <td className="pr-3 py-1 sticky left-0 bg-background whitespace-nowrap">
                        {row.vendor_name}
                        {row.entity_name && (
                          <span className="ml-1 text-muted-foreground">({row.entity_name})</span>
                        )}
                      </td>
                      <td className="text-right pr-3 py-1 tabular-nums">{row.gap_count}</td>
                      {(result.months || []).map((m) => (
                        <Cell
                          key={m}
                          vendorKey={row.vendor_key}
                          month={m}
                          cell={row.cells ? row.cells[m] : null}
                          selected={selected && selected.vendorKey === row.vendor_key && selected.month === m}
                          onSelect={onSelectCell}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selected && (
              <div
                className="p-3 rounded text-xs border bg-muted/40 space-y-1 font-mono"
                data-testid="rpc-cell-detail"
              >
                <p className="font-sans font-semibold">
                  {selected.vendorKey} &middot; {selected.month} &middot;{' '}
                  {_cellMeta(selected.cell).label}
                </p>
                {selected.cell.tx_ids && selected.cell.tx_ids.length > 0 && (
                  <p>tx: {selected.cell.tx_ids.join(', ')}</p>
                )}
                {selected.cell.doc_ids && selected.cell.doc_ids.length > 0 && (
                  <p>doc: {selected.cell.doc_ids.join(', ')}</p>
                )}
                {selected.cell.settled_via && (
                  <p className="font-sans">settled via: {selected.cell.settled_via}</p>
                )}
                {selected.cell.receipt_match_reasons && selected.cell.receipt_match_reasons.length > 0 && (
                  <p className="font-sans">receipt-paid reason: {selected.cell.receipt_match_reasons.join(', ')}</p>
                )}
                {selected.cell.anomaly_tx_id && (
                  <p className="font-sans">
                    anomaly tx {selected.cell.anomaly_tx_id}: +{selected.cell.anomaly_amount}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
